import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Typography, Button, Breadcrumbs, Menu, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2/dist/css/select2.min.css";
import "select2";
import Swal from "sweetalert2";
import "./RaiseComplaint.css";
import "./ProductTypeMasterManagement.css";
import { checklistDocumentAPI, masterDataAPI } from "../services/api";
import FileUploadPanelLayout from "../components/FileUploadPanelLayout";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const normalizeDocument = (item) => ({
  sLNo: item.sLNo || item.SLNo || 0,
  documentName: item.documentName || item.DocumentName || item.DOCUMENTNAME || "",
  documentDescription: item.documentDescription || item.DocumentDescription || item.DOCUMENTDESCRIPTION || "",
  productTypeCode: item.productTypeCode || item.ProductTypeCode || item.PRODUCTTYPECODE || "",
  catgCode: item.catgCode || item.CatgCode || item.CATGCODE || "",
  productCode: item.productCode || item.ProductCode || item.PRODUCTCODE || "",
  bvCode: item.bvCode || item.BVCode || item.BVCODE || "",
  filePath: item.filePath || item.FilePath || item.FILEPATH || "",
  fileName: item.fileName || item.FileName || item.FILENAME || "",
  fileSize: item.fileSize || item.FileSize || item.FILESIZE || 0,
  fileType: item.fileType || item.FileType || item.FILETYPE || "",
  version: item.version || item.Version || item.VERSION || "",
  isActive: item.isActive ?? item.IsActive ?? false,
  createdBy: item.createdBy || item.CreatedBy || item.CREATEDBY || "",
  createdOn: item.createdOn || item.CreatedOn || null,
  modifiedBy: item.modifiedBy || item.ModifiedBy || item.MODIFIEDBY || "",
  modifiedOn: item.modifiedOn || item.ModifiedOn || null,
  displayOrder: item.displayOrder || item.DisplayOrder || item.DISPLAYORDER || 0,
});

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const formatFileSize = (bytes) => {
  if (!bytes) return "-";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
  return (bytes / (1024 * 1024)).toFixed(2) + " MB";
};

const ChecklistDocumentManagement = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    catgCode: "",
    productTypeCode: "",
    bvCode: "",
  });
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [businessVerticals, setBusinessVerticals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editDocument, setEditDocument] = useState(null);
  const tableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const statusSelectRef = useRef(null);
  const catgSelectRef = useRef(null);
  const productTypeSelectRef = useRef(null);
  const bvSelectRef = useRef(null);
  const fileInputRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    loadCategories();
    loadProductTypes();
    loadBusinessVerticals();
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCategories = async () => {
    try {
      const response = await masterDataAPI.getCategories();
      if (response?.success && response.data) {
        setCategories(response.data);
      }
    } catch (e) {
      console.error("Error loading categories:", e);
    }
  };

  const loadProductTypes = async () => {
    try {
      const response = await masterDataAPI.getProductTypes();
      if (response?.success && response.data) {
        setProductTypes(response.data);
      }
    } catch (e) {
      console.error("Error loading product types:", e);
    }
  };

  const loadBusinessVerticals = async () => {
    try {
      const response = await masterDataAPI.getVerticals();
      if (response?.success && response.data) {
        setBusinessVerticals(response.data);
      }
    } catch (e) {
      console.error("Error loading business verticals:", e);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await checklistDocumentAPI.getAll({
        searchText: filters.search || undefined,
        status: filters.status,
        catgCode: filters.catgCode || undefined,
        productTypeCode: filters.productTypeCode || undefined,
        bvCode: filters.bvCode || undefined,
        pageNumber,
        pageSize,
      });

      if (response?.success && response.data?.data) {
        const normalized = response.data.data.map(normalizeDocument);
        setDocuments(normalized);
        setTotalRecords(response.data.totalRecords || 0);
        initializeDataTable(normalized);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load checklist documents",
      });
    } finally {
      setLoading(false);
    }
  };

  const initializeDataTable = (data) => {
    if (dataTableInstanceRef.current) {
      dataTableInstanceRef.current.destroy();
    }

    if (tableRef.current) {
      dataTableInstanceRef.current = new DataTable(tableRef.current, {
        data: data,
        columns: [
          {
            title: "Document Name",
            data: "documentName",
            createdCell: (td) => {
              td.classList.add("text-start");
            },
          },
          {
            title: "Category",
            data: "catgCode",
            createdCell: (td) => {
              td.classList.add("text-center");
            },
            render: (data) => data || "-",
          },
          {
            title: "Product Type",
            data: "productTypeCode",
            createdCell: (td) => {
              td.classList.add("text-center");
            },
            render: (data) => data || "-",
          },
          {
            title: "File Name",
            data: "fileName",
            createdCell: (td) => {
              td.classList.add("text-start");
            },
          },
          {
            title: "File Size",
            data: "fileSize",
            className: "text-end",
            render: (data) => formatFileSize(data),
          },
          {
            title: "Version",
            data: "version",
            className: "text-center",
            render: (data) => data || "-",
          },
          {
            title: "Status",
            data: "isActive",
            className: "text-center",
            render: (data) => {
              return data ? (
                '<span class="badge bg-success">Active</span>'
              ) : (
                '<span class="badge bg-secondary">Inactive</span>'
              );
            },
          },
          {
            title: "Created On",
            data: "createdOn",
            className: "text-center",
            render: (data) => formatDateTime(data),
          },
          {
            title: "Actions",
            data: null,
            orderable: false,
            className: "text-center",
            render: (data, type, row) => {
              return `
                <div class="d-flex gap-2 justify-content-center">
                  <button class="btn btn-sm btn-primary download-btn" data-id="${row.sLNo}" title="Download">
                    <i class="bi bi-download"></i>
                  </button>
                  <button class="btn btn-sm btn-warning edit-btn" data-id="${row.sLNo}" title="Edit">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button class="btn btn-sm btn-danger delete-btn" data-id="${row.sLNo}" title="Delete">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              `;
            },
          },
        ],
        order: [[7, "desc"]], // Sort by Created On descending
        pageLength: pageSize,
        responsive: true,
        language: {
          emptyTable: "No checklist documents found",
        },
      });

      // Attach event handlers
      $(tableRef.current).on("click", ".download-btn", function () {
        const id = $(this).data("id");
        handleDownload(id);
      });

      $(tableRef.current).on("click", ".edit-btn", function () {
        const id = $(this).data("id");
        handleEdit(id);
      });

      $(tableRef.current).on("click", ".delete-btn", function () {
        const id = $(this).data("id");
        handleDelete(id);
      });
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    fetchDocuments();
  };

  const handleReset = () => {
    setFilters({
      search: "",
      status: "all",
      catgCode: "",
      productTypeCode: "",
      bvCode: "",
    });
    setTimeout(() => {
      fetchDocuments();
    }, 100);
  };

  const handleDownload = async (slNo) => {
    try {
      const blob = await checklistDocumentAPI.download(slNo);
      const document = documents.find((d) => d.sLNo === slNo);
      const fileName = document?.fileName || "document";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading document:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to download document",
      });
    }
  };

  const handleEdit = (slNo) => {
    const document = documents.find((d) => d.sLNo === slNo);
    if (document) {
      setEditDocument(document);
      setUploadModalOpen(true);
    }
  };

  const handleDelete = async (slNo) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will deactivate the checklist document. You can reactivate it later.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, deactivate it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await checklistDocumentAPI.delete(slNo);
        if (response?.success) {
          Swal.fire("Deactivated!", "The document has been deactivated.", "success");
          fetchDocuments();
        } else {
          Swal.fire("Error", response?.message || "Failed to deactivate document", "error");
        }
      } catch (error) {
        console.error("Error deleting document:", error);
        Swal.fire("Error", "Failed to deactivate document", "error");
      }
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [".docx", ".pdf", ".doc"];
    const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      Swal.fire({
        icon: "error",
        title: "Invalid File Type",
        text: "Only .docx, .pdf, and .doc files are allowed",
      });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentName", editDocument?.documentName || file.name);
      formData.append("documentDescription", editDocument?.documentDescription || "");
      formData.append("catgCode", editDocument?.catgCode || "");
      formData.append("productTypeCode", editDocument?.productTypeCode || "");
      formData.append("productCode", editDocument?.productCode || "");
      formData.append("bvCode", editDocument?.bvCode || "");
      formData.append("version", editDocument?.version || "");
      formData.append("displayOrder", editDocument?.displayOrder || "0");
      formData.append("isActive", editDocument?.isActive !== undefined ? editDocument.isActive.toString() : "true");
      formData.append("createdBy", currentUser?.userIdCode || currentUser?.userName || "admin");

      let response;
      if (editDocument) {
        // Update existing document
        response = await checklistDocumentAPI.update(editDocument.sLNo, {
          documentName: editDocument.documentName,
          documentDescription: editDocument.documentDescription,
          catgCode: editDocument.catgCode,
          productTypeCode: editDocument.productTypeCode,
          productCode: editDocument.productCode,
          bvCode: editDocument.bvCode,
          version: editDocument.version,
          displayOrder: editDocument.displayOrder,
          isActive: editDocument.isActive,
        });
      } else {
        // Upload new document
        response = await checklistDocumentAPI.upload(formData);
      }

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: editDocument ? "Updated!" : "Uploaded!",
          text: editDocument ? "Document updated successfully" : "Document uploaded successfully",
        });
        setUploadModalOpen(false);
        setEditDocument(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        fetchDocuments();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to upload document",
        });
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to upload document",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Checklist Documents");

      worksheet.columns = [
        { header: "Document Name", key: "documentName", width: 30 },
        { header: "Category", key: "catgCode", width: 15 },
        { header: "Product Type", key: "productTypeCode", width: 20 },
        { header: "File Name", key: "fileName", width: 30 },
        { header: "File Size", key: "fileSize", width: 15 },
        { header: "Version", key: "version", width: 15 },
        { header: "Status", key: "isActive", width: 15 },
        { header: "Created On", key: "createdOn", width: 20 },
      ];

      documents.forEach((doc) => {
        worksheet.addRow({
          documentName: doc.documentName,
          catgCode: doc.catgCode || "-",
          productTypeCode: doc.productTypeCode || "-",
          fileName: doc.fileName,
          fileSize: formatFileSize(doc.fileSize),
          version: doc.version || "-",
          isActive: doc.isActive ? "Active" : "Inactive",
          createdOn: formatDateTime(doc.createdOn),
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ChecklistDocuments_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setExportMenuAnchor(null);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to export to Excel",
      });
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.text("Checklist Documents", 14, 15);

      const tableData = documents.map((item) => [
        item.documentName,
        item.catgCode || "-",
        item.productTypeCode || "-",
        item.fileName,
        formatFileSize(item.fileSize),
        item.version || "-",
        item.isActive ? "Active" : "Inactive",
        formatDateTime(item.createdOn),
      ]);

      autoTable(doc, {
        head: [["Document Name", "Category", "Product Type", "File Name", "File Size", "Version", "Status", "Created On"]],
        body: tableData,
        startY: 20,
      });

      doc.save(`ChecklistDocuments_${new Date().toISOString().split("T")[0]}.pdf`);
      setExportMenuAnchor(null);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to export to PDF",
      });
    }
  };

  // Initialize Select2 dropdowns
  useEffect(() => {
    if (statusSelectRef.current) {
      const $select = $(statusSelectRef.current);
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
      $select.select2({
        width: "100%",
        minimumResultsForSearch: Infinity,
      });
      $select.on("change", function (e) {
        handleFilterChange("status", e.target.value);
      });
      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
  }, []);

  useEffect(() => {
    if (catgSelectRef.current && categories.length > 0) {
      const $select = $(catgSelectRef.current);
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
      $select.select2({
        width: "100%",
        placeholder: "All Categories",
        allowClear: true,
      });
      $select.on("change", function (e) {
        handleFilterChange("catgCode", e.target.value || "");
      });
      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
  }, [categories]);

  useEffect(() => {
    if (productTypeSelectRef.current && productTypes.length > 0) {
      const $select = $(productTypeSelectRef.current);
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
      $select.select2({
        width: "100%",
        placeholder: "All Product Types",
        allowClear: true,
      });
      $select.on("change", function (e) {
        handleFilterChange("productTypeCode", e.target.value || "");
      });
      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
  }, [productTypes]);

  useEffect(() => {
    if (bvSelectRef.current && businessVerticals.length > 0) {
      const $select = $(bvSelectRef.current);
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
      $select.select2({
        width: "100%",
        placeholder: "All Business Verticals",
        allowClear: true,
      });
      $select.on("change", function (e) {
        handleFilterChange("bvCode", e.target.value || "");
      });
      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
  }, [businessVerticals]);

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Typography color="text.primary">Master Data</Typography>
        <Typography color="text.primary">Checklist Document Management</Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h5" component="h1">
            Checklist Document Management
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditDocument(null);
                setUploadModalOpen(true);
              }}
              sx={{
                background: "linear-gradient(135deg, #7a00f5 0%, #0f00f5 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #8a10ff 0%, #1f10ff 100%)",
                },
              }}
            >
              Upload Document
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            >
              Export
            </Button>
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={() => setExportMenuAnchor(null)}
            >
              <MenuItem onClick={handleExportExcel}>Export to Excel</MenuItem>
              <MenuItem onClick={handleExportPDF}>Export to PDF</MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Search</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search documents..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
                <button className="btn btn-outline-secondary" type="button" onClick={handleSearch}>
                  <SearchIcon />
                </button>
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select ref={statusSelectRef} className="form-select">
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Category</label>
              <select ref={catgSelectRef} className="form-select">
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.catgCode || cat.CatgCode} value={cat.catgCode || cat.CatgCode}>
                    {cat.catgName || cat.CatgName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Product Type</label>
              <select ref={productTypeSelectRef} className="form-select">
                <option value="">All Product Types</option>
                {productTypes.map((pt) => (
                  <option key={pt.productTypeCode || pt.ProductTypeCode} value={pt.productTypeCode || pt.ProductTypeCode}>
                    {pt.productTypeName || pt.ProductTypeName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Business Vertical</label>
              <select ref={bvSelectRef} className="form-select">
                <option value="">All Business Verticals</option>
                {businessVerticals.map((bv) => (
                  <option key={bv.bvCode || bv.BVCode} value={bv.bvCode || bv.BVCode}>
                    {bv.bvName || bv.BVName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-1">
              <label className="form-label" style={{ visibility: "hidden" }}>
                Reset
              </label>
              <button className="btn btn-outline-secondary w-100" onClick={handleReset} title="Reset">
                <RestartAltIcon />
              </button>
            </div>
          </div>
        </Box>

        {/* Data Table */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </Box>
        ) : (
          <table ref={tableRef} className="table table-striped table-bordered" style={{ width: "100%" }}></table>
        )}
      </Paper>

      {/* Upload/Edit Modal */}
      {uploadModalOpen && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editDocument ? "Edit Checklist Document" : "Upload Checklist Document"}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setUploadModalOpen(false);
                    setEditDocument(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <FileUploadPanelLayout
                  title={editDocument ? "Replace document (optional)" : "Upload checklist document"}
                  formatNote="( Supported formats: .docx, .pdf, .doc )"
                  helperText="Choose a file from your computer"
                  inputRef={fileInputRef}
                  accept=".docx,.pdf,.doc"
                  multiple={false}
                  onChange={handleUpload}
                  disabled={uploading}
                  chooseStyle={{ opacity: uploading ? 0.6 : 1 }}
                  footerContent={
                    <>
                      {editDocument && (
                        <div className="alert alert-info mb-0">
                          <strong>Current File:</strong> {editDocument.fileName} ({formatFileSize(editDocument.fileSize)})
                        </div>
                      )}
                      {uploading && (
                        <div className="text-center py-2">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Uploading...</span>
                          </div>
                          <p className="mt-2 mb-0">Uploading document...</p>
                        </div>
                      )}
                    </>
                  }
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setUploadModalOpen(false);
                    setEditDocument(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                  disabled={uploading}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default ChecklistDocumentManagement;
